import type React from 'react'
import {useState, useEffect} from 'react'
import {useSelector, useDispatch} from 'react-redux'
import type {DropdownItem} from 'pepsico-ds'
import LoadingSpinner from '@/components/GDS/atoms/LoadingSpinner'
import teamStepConfig from './teamStepConfig'
import {
  getBuildPayload,
  getDeployPayload,
  getRepoPayload,
  getAppsecPayload,
  getQAPayload,
  getFeaturePayload,
  getSecurityPayload,
} from '../util/widgetPayloadUtils'
import {setOnboardingDashboardInfo} from '@/store/onboardingSlices'
import {
  linkTeamDashboard,
  updateDashboardWidgets,
  updateTeamDashboardWidgets,
  upsertMultipleWidgets,
} from '@/services/team/teamService'
import StepComponent from '../StepComponent'
import OnboardingModalShell from '../OnboardingModalShell'
import OnboardingCompletionFlow from '../OnboardingCompletionFlow'
import {resetAllOnboardingSlices} from '@/store/resetOnboardingSlices'
import {setOldProgramReference} from '@/store/programOnboardingSlice'
import {validateEmails} from '@/components/SelfOnboarding/util/onboardingUtils'

interface TeamOnboardingFlowProps {
  open: boolean
  onClose: () => void
  onboardingDashboard?: any
  onToastFinish?: (dashboardName: string) => void
}

const TeamOnboardingFlow: React.FC<TeamOnboardingFlowProps> = ({open, onClose, onboardingDashboard, onToastFinish}) => {
  const [hasSameProduct, setHasSameProduct] = useState(false)

  const [loading, setLoading] = useState(false)
  const dispatch = useDispatch()
  const [step, setStep] = useState(0)
  const [editMode, setEditMode] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [values, setValues] = useState<any>({
    teamName: '',
    ownerName: '',
    email: '',
    scoreEnabled: false,
    teamDescription: '',
  })
  const [errors, setErrors] = useState<any>({})
  const [touched, setTouched] = useState<any>({})
  const [teamNameError, setTeamNameError] = useState<string | null>(null)
  const [widgetConfigErrors, setWidgetConfigErrors] = useState<any>({})
  const build = useSelector((state: any) => state.build)
  const deploy = useSelector((state: any) => state.deploy)
  const repo = useSelector((state: any) => state.repo)
  const appsec = useSelector((state: any) => state.appsec)
  const qa = useSelector((state: any) => state.qa)
  const feature = useSelector((state: any) => state.feature)
  const securityAnalysis = useSelector((state: any) => state.security)
  const oldProgramReference = useSelector((state: any) => state.oldProgramReference)
  // Reset values on modal close and prepare for fresh data on open
  useEffect(() => {
    if (!open) {
      dispatch({
        type: 'onboardingDashboard/setOnboardingDashboardInfo',
        payload: {
          teamName: '',
          ownerName: '',
          email: '',
          scoreEnabled: false,
          teamDescription: '',
        },
      })
      setErrors({})
      setTouched({})
      setLoaded(false) // Reset loaded state so fresh data is fetched next time
      setValues({teamName: '', ownerName: '', email: '', scoreEnabled: false, teamDescription: ''})
    } else if (open && onboardingDashboard?.editMode) {
      // When modal opens in edit mode, ensure we fetch fresh data
      setLoaded(false)
    }
  }, [open, dispatch, onboardingDashboard?.editMode])

  useEffect(() => {
    const id = onboardingDashboard?.dashboardId
    if (id && !loaded && open) {
      // Only load when modal is actually open
      setLoading(true)
      ;(async () => {
        const {getDashboard} = await import('@/services/team/teamService')
        // Always fetch fresh data in edit mode to avoid stale cache
        const data = await getDashboard(id, onboardingDashboard?.editMode)
        if (onboardingDashboard.editMode && data.widgets) {
          const {hydrateAllWidgetsFromDashboard} = await import('../util/widgetHydrationUtils')
          hydrateAllWidgetsFromDashboard(dispatch, data)
        }
        const owners = Array.isArray(data.owners) ? data.owners : []
        const ownerNames = owners
          .map((o: any) => o.ownerName)
          .filter(Boolean)
          .join(',')
        const ownerEmails = owners
          .map((o: any) => o.emails)
          .filter(Boolean)
          .join(',')
        dispatch({
          type: 'onboardingDashboard/setOnboardingDashboardInfo',
          payload: {
            teamName: data.title || '',
            ownerName: ownerNames,
            email: ownerEmails,
          },
        })
        setValues({
          teamName: data.title || '',
          ownerName: ownerNames,
          email: ownerEmails,
          scoreEnabled: false,
          teamDescription: '',
        })
        dispatch(
          setOldProgramReference({...oldProgramReference, oldConfigurationItemName: data.configurationItemBusAppName}),
        )
        setLoaded(true)
        setLoading(false)
      })()
    }
  }, [onboardingDashboard?.dashboardId, loaded, dispatch, onboardingDashboard?.editMode, open])

  // Validation for team onboarding
  const forbiddenChars = /[^a-zA-Z0-9 ]/
  const validate = (vals = values) => {
    const errs: any = {}
    if (typeof vals.teamName !== 'string' || !vals.teamName) errs.teamName = 'Team Name is required.'
    else if (typeof vals.teamName === 'string' && vals.teamName.length < 3)
      errs.teamName = 'Team Name must be at least 3 characters.'
    else if (forbiddenChars.test(vals.teamName)) {
      errs.teamName = 'Invalid team name: Special characters are not allowed.'
    }
    if (typeof vals.ownerName !== 'string' || !vals.ownerName) errs.ownerName = 'Owner Name is required.'
    else if (typeof vals.ownerName === 'string' && vals.ownerName.length < 3)
      errs.ownerName = 'Owner Name must be at least 3 characters.'
    if (typeof vals.email !== 'string' || !vals.email) errs.email = 'Email is required.'
    else if (typeof vals.email === 'string' && !validateEmails(vals.email)) errs.email = 'Please enter valid email(s).'
    return errs
  }

  const closePopup = async () => {
    if (step !== 0 && onboardingDashboard.dashboardId && !onboardingDashboard.editMode) {
      try {
        const {deleteDashboard} = await import('@/services/team/teamService')
        await deleteDashboard(onboardingDashboard.dashboardId)
      } catch (error) {
        // Error handling is silent
      }
      resetAllOnboardingSlices(dispatch)
      onClose()
    } else {
      resetAllOnboardingSlices(dispatch)
      onClose()
    }
  }

  // --- Save logic ---
  const getStepOnePayload = () => {
    const ownerNames =
      typeof values.ownerName === 'string'
        ? values.ownerName
            .split(',')
            .map((s: string) => s.trim())
            .filter(Boolean)
        : []
    const emails =
      typeof values.email === 'string'
        ? values.email
            .split(',')
            .map((s: string) => s.trim())
            .filter(Boolean)
        : []
    return {
      template: 'Widgets',
      title: values.teamName,
      type: 'Team',
      applicationName: values.teamName,
      componentName: values.teamName,
      scoreEnabled: values.scoreEnabled,
      scoreDisplay: 'HEADER',
      ownerName: ownerNames,
      emails: emails,
      activeWidgets: [''],
    }
  }

  function prepareWidgetConfigsForUpsert(allWidgets: any[]): any[] {
    return allWidgets.map(widgetConfig => {
      const widgetId = widgetConfig?.options?.id
      const componentId = onboardingDashboard.component?.id
      if (widgetId && widgetId.length >= 24 && widgetConfig.widgetDefaultId) {
        const updatedWidgetConfig = {
          ...widgetConfig,
          componentId: widgetConfig.componentId || componentId,
          options: {
            ...widgetConfig.options,
            id: widgetConfig.widgetDefaultId,
          },
        }
        delete updatedWidgetConfig.widgetDefaultId
        return updatedWidgetConfig
      } else {
        const {widgetDefaultId, ...rest} = widgetConfig
        return {
          ...rest,
          componentId: widgetConfig.componentId || componentId,
        }
      }
    })
  }

  const saveWidgets = async () => {
    try {
      // Build all widget payloads in parallel and await them
      const widgetPayloadPromises = []
      if (build && build.pipelines && build.pipelines.length > 0 && build.buildDurationThreshold !== '') {
        widgetPayloadPromises.push(
          getBuildPayload(build, onboardingDashboard.component?.id, !!onboardingDashboard.editMode),
        )
      }
      if (deploy && deploy.length > 0 && deploy[0].deployJob) {
        widgetPayloadPromises.push(
          getDeployPayload(deploy, onboardingDashboard.component?.id, !!onboardingDashboard.editMode),
        )
      }
      if (repo && repo.length > 0 && repo[0].repoUrl) {
        widgetPayloadPromises.push(
          getRepoPayload(repo, onboardingDashboard.component?.id, !!onboardingDashboard.editMode),
        )
      }
      if (appsec && appsec.length > 0) {
        widgetPayloadPromises.push(
          getAppsecPayload(appsec, onboardingDashboard.component?.id, !!onboardingDashboard.editMode),
        )
      }
      if (qa && qa.length > 0) {
        widgetPayloadPromises.push(await getQAPayload(qa, onboardingDashboard.component?.id))
      }
      if (feature && feature.length > 0) {
        widgetPayloadPromises.push(
          getFeaturePayload(feature, onboardingDashboard.component?.id, !!onboardingDashboard.editMode),
        )
      }
      if (securityAnalysis && securityAnalysis.length > 0) {
        widgetPayloadPromises.push(
          await getSecurityPayload(securityAnalysis, onboardingDashboard.component?.id, !!onboardingDashboard.editMode),
        )
      }

      const allWidgets = (await Promise.all(widgetPayloadPromises)).filter(Boolean)

      const preparedWidgets = prepareWidgetConfigsForUpsert(allWidgets)
      await upsertMultipleWidgets(
        onboardingDashboard.dashboardId as string,
        preparedWidgets,
        !!onboardingDashboard.editMode,
      )

      // After widgets upsert, update activeWidgets on dashboard
      const activeWidgets = preparedWidgets.map(w => w.name).filter(Boolean)
      if (onboardingDashboard.editMode) {
        await updateDashboardWidgets(onboardingDashboard.dashboardId as string, {activeWidgets})
      } else {
        await updateTeamDashboardWidgets(onboardingDashboard.dashboardId as string, {activeWidgets})
      }
    } catch (error: any) {
      // Error handling can be added here if needed in the future
      console.error('Failed to save widgets:', error)
    }
  }

  function getUniqueOwnersByEmail(
    owners: {ownerName: string; emails: string}[],
  ): {ownerName: string; emails: string}[] {
    const seen = new Set<string>()
    return owners.filter(owner => {
      if (!owner.emails) return true
      if (seen.has(owner.emails)) return false
      seen.add(owner.emails)
      return true
    })
  }

  const saveTeamDashboard = async () => {
    const ownerNames =
      typeof values.ownerName === 'string'
        ? values.ownerName
            .split(',')
            .map((s: string) => s.trim())
            .filter(Boolean)
        : []
    const emails =
      typeof values.email === 'string'
        ? values.email
            .split(',')
            .map((s: string) => s.trim())
            .filter(Boolean)
        : []
    const updatedOwners = ownerNames.map((ownerName: string, idx: number) => ({ownerName, emails: emails[idx] || ''}))
    const owners = getUniqueOwnersByEmail(updatedOwners)
    let dashboardResponse
    if (onboardingDashboard.editMode) {
      const {editDashboard, getDashboard} = await import('@/services/team/teamService')
      let fullDashboard = onboardingDashboard?.dashboard
      if (!fullDashboard) {
        fullDashboard = await getDashboard(onboardingDashboard.dashboardId as string)
      }
      if (!fullDashboard) return true
      const editableDashboard = {
        ...fullDashboard,
        title: onboardingDashboard.teamName || fullDashboard.title,
        owners: owners,
        application: {...fullDashboard.application},
      }
      if (editableDashboard.application) {
        editableDashboard.application = {
          name: onboardingDashboard.teamName || fullDashboard.application.name,
          components: [
            {
              name: onboardingDashboard.component?.name || fullDashboard.application.components[0].name,
              id: fullDashboard.application.components[0].id,
            },
          ],
        }
      }

      dashboardResponse = editableDashboard
      await editDashboard(onboardingDashboard.dashboardId as string, editableDashboard)
    } else {
      // Check for duplicate dashboard name before creating
      const {getDashboardByName} = await import('@/services/team/teamService')
      try {
        const matches = await getDashboardByName(values.teamName)
        if (matches && matches.length > 0) {
          setErrors((prev: any) => ({
            ...prev,
            teamName: 'A dashboard with this name already exists.',
          }))
          return false
        }
      } catch (error) {
        // Optionally handle API error (network, etc)
        setErrors((prev: any) => ({
          ...prev,
          teamName: 'Error checking dashboard name. Please try again.',
        }))
        return false
      }
      const {createTeamDashboard} = await import('@/services/team/teamService')
      const payload = getStepOnePayload()
      dashboardResponse = await createTeamDashboard(payload)
    }
    dispatch(
      setOnboardingDashboardInfo({
        dashboardId: onboardingDashboard.dashboardId || dashboardResponse.id,
        component: onboardingDashboard.component || dashboardResponse?.application?.components?.[0],
        dashboard: dashboardResponse,
        editMode: onboardingDashboard.editMode,
      }),
    )
    return true
  }

  // Main step advance handler
  const handleNext = async () => {
    const validationErrors = validate(values)
    setTouched((prev: any) => ({...prev, teamName: true, ownerName: true, email: true, teamDescription: true}))
    setErrors(validationErrors)
    if (Object.keys(validationErrors).length > 0) return
    setLoading(true)
    try {
      if (step === 0) {
        const success = await saveTeamDashboard()
        if (!success) return
      } else if (step === 1) {
        await saveWidgets()
      } else if (
        step === 2 &&
        (onboardingDashboard.productDashboardId || oldProgramReference.oldProductId) &&
        !hasSameProduct
      ) {
        await linkWithProgram()
      }

      // Check if this is the final step (Finish button)
      if (step === teamStepConfig.length - 1) {
        // This is the finish step - call onToastFinish callback
        const dashboardName = values.teamName || onboardingDashboard?.teamName || 'Team'
        if (onToastFinish) {
          onToastFinish(dashboardName)
        }
        return // Don't advance to next step, we're done
      }

      setStep(s => s + 1)
    } finally {
      setLoading(false)
    }
  }

  const linkWithProgram = async () => {
    const teamID = onboardingDashboard.dashboardId
    const response = await linkTeamDashboard({
      oldProductId: oldProgramReference.oldProductId,
      linkAction: oldProgramReference.linkAction || 'ADD',
      productId: oldProgramReference.linkAction === 'REMOVE' ? undefined : onboardingDashboard.productDashboardId,
      dashboardIds: [teamID],
    })

    if (response) {
      dispatch(
        setOldProgramReference({
          ...oldProgramReference,
          oldConfigurationItemName:
            oldProgramReference.linkAction === 'DELETE' ? undefined : onboardingDashboard.programDashboardName,
        }),
      )
    }
  }

  const onProductIdChange = (id: string, label: string, isSameProduct: boolean) => {
    const programName = onboardingDashboard?.dashboard?.configurationItemBusAppName
    const isProgramRemoved = programName && !label

    if ((programName && label != programName) || !programName || isSameProduct || isProgramRemoved) {
      dispatch({
        type: 'onboardingDashboard/setOnboardingDashboardInfo',
        payload: {
          ...onboardingDashboard,
          productDashboardId: id,
          programDashboardName: label || id,
        },
      })
    }
    setHasSameProduct(isSameProduct)
  }

  // Handlers
  const handleTeamInputChange = (e: any) => {
    const {name, value} = e.target
    dispatch({type: 'onboardingDashboard/setOnboardingDashboardInfo', payload: {[name]: value}})
    setTouched((prev: any) => ({...prev, [name]: true}))
    setErrors(validate({...values, [name]: value}))
    setValues((prev: any) => ({...prev, [name]: value}))
  }
  const handleDropdownChange = (val: DropdownItem[] | undefined) => {
    handleTeamInputChange({target: {name: 'ownerDepartment', value: val?.[0]?.id}})
  }
  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {name} = e.target
    setTouched((t: any) => ({...t, [name]: true}))
  }

  return (
    <OnboardingCompletionFlow>
      <OnboardingModalShell
        open={open}
        onClose={closePopup}
        step={step}
        setStep={setStep}
        stepConfig={teamStepConfig}
        title={onboardingDashboard?.editMode ? 'Edit Teams Dashboard' : 'Add New Teams Dashboard'}
        primaryButtonProps={{
          size: 'medium',
          text: step === teamStepConfig.length - 1 ? 'Finish' : 'Continue',
          variant: 'primary',
          onClick: handleNext,
          disabled:
            loading || Object.keys(errors).length > 0 || Object.keys(widgetConfigErrors).length > 0 || !!teamNameError,
        }}
        secondaryButtonProps={{
          size: 'medium',
          text: 'Cancel',
          variant: 'secondary',
          onClick: closePopup,
        }}
      >
        <StepComponent
          step={step}
          values={values}
          errors={errors}
          touched={touched}
          onChange={handleTeamInputChange}
          onBlur={handleBlur}
          onTeamNameError={setTeamNameError}
          setWidgetConfigErrors={setWidgetConfigErrors}
          editMode={editMode}
          onboardingDashboard={onboardingDashboard}
          productId={undefined}
          setProductId={undefined}
          dispatch={dispatch}
          setStep={setStep}
          stepConfig={teamStepConfig}
          handleDropdownChange={handleDropdownChange}
          onClose={closePopup}
          onProductIdChange={onProductIdChange}
        />
        {loading && (
          <div className="fixed inset-0 z-[10000] flex h-screen w-screen items-center justify-center">
            <LoadingSpinner centered />
          </div>
        )}
      </OnboardingModalShell>
    </OnboardingCompletionFlow>
  )
}

export default TeamOnboardingFlow
